import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Radio, Select } from 'antd';
import SchedularHeader from '../SchedularHeader/SchedularHeader';
import ResponseMessage from '../SlotSchedulerResponseMessage/SlotSchedulerResponseMessage';
import {
  getLinkId,
  getBookToken,
  forgetBookToken,
  toBookingView,
  toDayGroups,
  timeZoneChoices,
  toTimeLabel,
  toMeetingLink,
  describeLinkFailure,
  isSlotTaken,
} from '../../Utility/bookLink';
import { zoneLabel } from '../../Utility/zonedTime';
import { toInstant } from '../../Utility/proposeLink';
import './CandidateAvailabilityBooking.scss';

const UNUSABLE_LINK = describeLinkFailure({ response: { status: 401 } });

const NO_TIMES = {
  title: 'There are no times to choose from',
  description: 'Reply to the text message that brought you here and your recruiter will send new times.',
};

const SLOT_TAKEN = 'That time was just taken. Pick another.';
const CONFIRMATION_FOLLOWS = 'A confirmation is on its way by text message and email.';

const CandidateAvailabilityBooking = ({ repository }) => {
  const linkId = getLinkId();
  // The fragment disappears on first paint, so later renders keep this value.
  const [token] = useState(getBookToken);

  const [resolveState, setResolveState] = useState({ status: 'INPROGRESS', view: null, failure: null });
  const [resolveAttempt, setResolveAttempt] = useState(0);
  const [slotsState, setSlotsState] = useState({ status: 'INPROGRESS', slots: null, failure: null });
  const [slotsAttempt, setSlotsAttempt] = useState(0);
  // null is the zone the link resolved to, so no render formats with a zone it does not have yet.
  const [chosenZone, setChosenZone] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookState, setBookState] = useState({ status: 'IDLE', failure: null, booked: null });

  const isStaleRef = useRef(false);
  // State cannot stop two clicks in the same tick from sending twice.
  const isBookInFlightRef = useRef(false);

  useEffect(
    () => () => {
      isStaleRef.current = true;
    },
    []
  );

  useEffect(() => {
    if (token) forgetBookToken();
  }, [token]);

  useEffect(() => {
    if (!linkId || !token) return undefined;
    let isStale = false;
    setResolveState({ status: 'INPROGRESS', view: null, failure: null });
    repository
      .resolveAvailabilityLink(linkId, token)
      .then(response => {
        if (!isStale) setResolveState({ status: 'COMPLETED', view: toBookingView(response.data), failure: null });
      })
      .catch(error => {
        if (!isStale) setResolveState({ status: 'FAILED', view: null, failure: describeLinkFailure(error) });
      });
    return () => {
      isStale = true;
    };
  }, [repository, linkId, token, resolveAttempt]);

  const { view } = resolveState;
  const expiresAtUtc = view ? view.expiresAtUtc : null;

  useEffect(() => {
    if (!expiresAtUtc) return undefined;
    let isStale = false;
    setSlotsState(previous => ({ status: 'INPROGRESS', slots: previous.slots, failure: null }));
    repository
      .getAvailabilityForLink(linkId, token, new Date().toISOString(), expiresAtUtc)
      .then(response => {
        if (isStale) return;
        const slots = response && response.data && Array.isArray(response.data.slots) ? response.data.slots : [];
        setSlotsState({ status: 'COMPLETED', slots, failure: null });
      })
      .catch(error => {
        if (!isStale) setSlotsState({ status: 'FAILED', slots: null, failure: describeLinkFailure(error) });
      });
    return () => {
      isStale = true;
    };
  }, [repository, linkId, token, expiresAtUtc, slotsAttempt]);

  const timeZone = chosenZone || (view ? view.defaultTimeZone : null);
  const days = useMemo(() => toDayGroups(slotsState.slots, timeZone), [slotsState.slots, timeZone]);
  const zoneChoices = useMemo(() => (view ? timeZoneChoices(view.defaultTimeZone) : []), [view]);

  const activeDay = days.find(day => day.date === selectedDate) || days[0] || null;

  const chooseZone = zone => {
    setChosenZone(zone);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const chooseDay = event => {
    setSelectedDate(event.target.value);
    setSelectedSlot(null);
  };

  const bookSelectedSlot = () => {
    if (!selectedSlot || isBookInFlightRef.current) return;
    isBookInFlightRef.current = true;
    setBookState({ status: 'INPROGRESS', failure: null, booked: null });
    repository
      .bookFromAvailabilityLink(linkId, token, selectedSlot.startUtc, timeZone)
      .then(response => {
        isBookInFlightRef.current = false;
        if (isStaleRef.current) return;
        // The server is the authority on which time was booked; the pick is only what was asked for.
        const booked = response && response.data ? response.data : {};
        const bookedStart = toInstant(booked.startsAtUtc);
        setBookState({
          status: 'BOOKED',
          failure: null,
          booked: {
            label: bookedStart ? toTimeLabel(timeZone, booked.startsAtUtc, booked.endsAtUtc) : null,
            meetingLink: toMeetingLink(booked.meetingLink),
          },
        });
      })
      .catch(error => {
        isBookInFlightRef.current = false;
        if (isStaleRef.current) return;
        if (isSlotTaken(error)) {
          setSelectedSlot(null);
          setBookState({ status: 'SLOT_TAKEN', failure: null, booked: null });
          setSlotsAttempt(attempt => attempt + 1);
          return;
        }
        setBookState({ status: 'FAILED', failure: describeLinkFailure(error), booked: null });
      });
  };

  const renderOutcome = ({ title, description }, children = null) => (
    <>
      <SchedularHeader showCalendar={false} />
      <div className="slot-schedular-body centered">
        <ResponseMessage title={title} description={description}>
          {children}
        </ResponseMessage>
      </div>
    </>
  );

  const renderLoader = () => (
    <div className="loader-container">
      <div className="loader" />
    </div>
  );

  const renderRetry = onRetry => (
    <Button className="book-retry" type="primary" onClick={onRetry}>
      Try again
    </Button>
  );

  if (!linkId || !token) return renderOutcome(UNUSABLE_LINK);

  if (bookState.status === 'BOOKED') {
    const { label, meetingLink } = bookState.booked;
    return renderOutcome(
      {
        title: 'Your interview is booked',
        description: label ? `We have booked ${label}. ${CONFIRMATION_FOLLOWS}` : CONFIRMATION_FOLLOWS,
      },
      meetingLink ? (
        <a className="book-meeting-link" href={meetingLink} target="_blank" rel="noopener noreferrer">
          {meetingLink}
        </a>
      ) : null
    );
  }

  if (bookState.status === 'FAILED' && !bookState.failure.canRetry) return renderOutcome(bookState.failure);

  if (resolveState.status === 'INPROGRESS') return renderLoader();

  if (resolveState.status === 'FAILED') {
    const { failure } = resolveState;
    return renderOutcome(failure, failure.canRetry ? renderRetry(() => setResolveAttempt(attempt => attempt + 1)) : null);
  }

  if (!expiresAtUtc) return renderOutcome(UNUSABLE_LINK);

  if (slotsState.status === 'FAILED') {
    const { failure } = slotsState;
    return renderOutcome(failure, failure.canRetry ? renderRetry(() => setSlotsAttempt(attempt => attempt + 1)) : null);
  }

  if (slotsState.slots === null) return renderLoader();

  if (!activeDay) return renderOutcome(NO_TIMES);

  const contact = view.recruiterName || 'your recruiter';
  const expiresAtLabel = toTimeLabel(timeZone, expiresAtUtc);
  const isBooking = bookState.status === 'INPROGRESS';
  const isRefreshing = slotsState.status === 'INPROGRESS';

  return (
    <>
      <SchedularHeader showCalendar={false} />
      <div className="slot-schedular-body">
        <div className="info">
          {view.slotDurationMinutes ? <div className="slot-duration">{view.slotDurationMinutes} Minutes</div> : null}
          {view.candidateName ? <div className="name">Hi, {view.candidateName}</div> : null}
          <div className="calendar-schedule-text">
            {view.jobTitle ? `Book your ${view.jobTitle} interview` : 'Book your interview'}
          </div>
          <div className="calendar-schedule-description">
            {contact} has shared the times they are free. Pick the one that suits you and book it.
            {expiresAtLabel ? ` This link works until ${expiresAtLabel}.` : ''}
          </div>
          <div className="book-timezone">
            <span id="book-timezone-label">Times are shown in</span>
            <Select
              aria-labelledby="book-timezone-label"
              showSearch
              value={timeZone}
              optionFilterProp="children"
              onChange={chooseZone}
            >
              {zoneChoices.map(zone => (
                <Select.Option key={zone} value={zone}>
                  {zoneLabel(zone)}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="separator" />
        <div className="book-picker">
          <Radio.Group className="book-days" value={activeDay.date} onChange={chooseDay}>
            {days.map(day => (
              <Radio.Button className="book-day" key={day.date} value={day.date}>
                <span className="book-day-label">{day.shortLabel}</span>
              </Radio.Button>
            ))}
          </Radio.Group>
          <div className="book-day-heading">{activeDay.label} {activeDay.zoneLabel}</div>
          <Radio.Group
            className="book-slots"
            value={selectedSlot ? selectedSlot.startUtc : null}
            onChange={event => setSelectedSlot(activeDay.slots.find(slot => slot.startUtc === event.target.value) || null)}
          >
            {activeDay.slots.map(slot => (
              <Radio className="book-slot" key={slot.startUtc} value={slot.startUtc} disabled={isRefreshing}>
                <span className="book-slot-label">{slot.label}</span>
              </Radio>
            ))}
          </Radio.Group>
          {bookState.status === 'SLOT_TAKEN' ? <p className="book-slot-taken">{SLOT_TAKEN}</p> : null}
          {bookState.status === 'FAILED' ? (
            <p className="book-failure">
              {bookState.failure.title}. {bookState.failure.description}
            </p>
          ) : null}
          <Button
            className="book-confirm"
            type="primary"
            disabled={!selectedSlot || isBooking || isRefreshing}
            onClick={bookSelectedSlot}
          >
            {isBooking ? 'Booking' : 'Book this time'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default CandidateAvailabilityBooking;
